import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Field, Input, NotificationContext, Row, Text, } from '@dossierhq/design';
import { useCallback, useContext, useState, } from 'react';
import { DossierContext } from '../../contexts/DossierContext.js';
import { EntityEditorActions, getEntityCreateFromDraftState, getEntityUpdateFromDraftState, } from '../../reducers/EntityEditorReducer/EntityEditorReducer.js';
import { AuthKeyPicker } from './AuthKeyPicker.js';
import { EntityFieldEditor } from './EntityFieldEditor.js';
import { PublishingButton } from './PublishingButton.js';
export function EntityEditor({ draftState, dispatchEntityEditorState }) {
    const { client } = useContext(DossierContext);
    const { showNotification } = useContext(NotificationContext);
    const [submitLoading, setSubmitLoading] = useState(false);
    const handleNameChange = useCallback((event) => dispatchEntityEditorState(new EntityEditorActions.SetName(draftState.id, event.target.value)), [dispatchEntityEditorState, draftState.id]);
    const handleAuthKeyChange = useCallback((authKey) => dispatchEntityEditorState(new EntityEditorActions.SetAuthKey(draftState.id, authKey)), [dispatchEntityEditorState, draftState.id]);
    const handleSubmitClick = useCallback(() => {
        submitEntity(draftState, setSubmitLoading, client, dispatchEntityEditorState, showNotification, false);
    }, [client, dispatchEntityEditorState, draftState, showNotification]);
    const handleSubmitAndPublishClick = useCallback(() => {
        submitEntity(draftState, setSubmitLoading, client, dispatchEntityEditorState, showNotification, true);
    }, [client, dispatchEntityEditorState, draftState, showNotification]);
    if (!draftState.draft) {
        return null;
    }
    const isNewEntity = !draftState.entity;
    const isSubmittable = !submitLoading && (isNewEntity || draftState.status === 'changed') && !draftState.hasSaveErrors;
    const isPublishable = !draftState.hasPublishErrors;
    return (_jsxs(_Fragment, { children: [_jsxs(Field, { children: [_jsx(Field.Label, { children: "Name" }), _jsxs(Field.Control, { children: [_jsx(Input, { value: draftState.draft.name, onChange: handleNameChange }), draftState.draft.nameIsLinkedToField ? (_jsxs(Text, { textStyle: "body2", marginTop: 1, children: ["The name is linked to the field: ", draftState.draft.entitySpec.nameField, "."] })) : null] })] }), !draftState.entity && draftState.draft.entitySpec.authKeyPattern ? (_jsxs(Field, { children: [_jsx(Field.Label, { children: "Authorization key" }), _jsx(Field.Control, { children: _jsx(AuthKeyPicker, { patternName: draftState.draft.entitySpec.authKeyPattern, value: draftState.draft.authKey, onValueChange: handleAuthKeyChange }) }), draftState.draft.authKey === null ? (_jsx(Field.Help, { color: "danger", children: "Authorization key is required" })) : null] })) : null, _jsxs(Row, { gap: 2, justifyContent: "center", children: [_jsx(Button, { color: "primary", disabled: !isSubmittable, onClick: handleSubmitClick, children: isNewEntity ? 'Create' : 'Save' }), draftState.draft.entitySpec.publishable ? (_jsx(Button, { disabled: !isSubmittable || !isPublishable, onClick: handleSubmitAndPublishClick, children: isNewEntity ? 'Create & publish' : 'Save & publish' })) : null, _jsx(PublishingButton, { disabled: draftState.status !== '', entity: draftState.entity, entitySpec: draftState.draft.entitySpec })] }), draftState.draft.fields.map((field) => (_jsx(EntityFieldEditor, { field: field, onValueChange: (value) => dispatchEntityEditorState(new EntityEditorActions.SetField(draftState.id, field.fieldSpec.name, value)) }, field.fieldSpec.name)))] }));
}
async function submitEntity(draftState, setSubmitLoading, client, dispatchEntityEditorState, showNotification, publish) {
    setSubmitLoading(true);
    dispatchEntityEditorState(new EntityEditorActions.SetNextEntityUpdateIsDueToUpsert(draftState.id, true));
    const isCreate = !draftState.entity;
    let result;
    if (isCreate) {
        const entityCreate = getEntityCreateFromDraftState(draftState);
        result = await client.createEntity(entityCreate, { publish });
    }
    else {
        const entityUpdate = getEntityUpdateFromDraftState(draftState);
        result = await client.updateEntity(entityUpdate, { publish });
    }
    if (result.isOk()) {
        const message = isCreate
            ? publish
                ? 'Created and published entity'
                : 'Created entity'
            : publish
                ? 'Updated and published entity'
                : 'Updated entity';
        showNotification({ color: 'success', message });
    }
    else {
        const message = isCreate
            ? publish
                ? 'Failed creating and publishing entity'
                : 'Failed creating entity'
            : publish
                ? 'Failed updating and publishing entity'
                : 'Failed updating entity';
        showNotification({ color: 'error', message });
        dispatchEntityEditorState(new EntityEditorActions.SetNextEntityUpdateIsDueToUpsert(draftState.id, false));
    }
    setSubmitLoading(false);
}
//# sourceMappingURL=EntityEditor.js.map